import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Topbar from '../components/Topbar'
import { useTranslation } from '../i18n/LanguageContext'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area, RadialBarChart, RadialBar
} from 'recharts'

// ── Design tokens (CSS variables) ───────────────────────────────────────────
const BLUE   = 'var(--primary)'
const TEXT   = 'var(--text-primary)'
const MUTED  = 'var(--text-muted)'
const BORDER = 'var(--border)'
const BG     = 'var(--bg-page)'
const WHITE  = 'var(--bg-card)'
const GREEN  = 'var(--success)'
const RED    = 'var(--error)'
const AMBER  = 'var(--warning)'
const PURPLE = '#7C3AED'
const FONT_UI   = 'var(--font-ui)'
const FONT_BODY = 'var(--font-body)'
const HEAT_BASE = '#0EA5E9' // base hue for heatmap intensity blends

const PALETTE = [BLUE, GREEN, PURPLE, AMBER, '#0F766E', RED, '#EC4899', '#06B6D4', '#8B5CF6', '#F97316']

const STATUS_COLORS = {
  received: BLUE, pending: AMBER, confirmed: '#F97316',
  processing: '#0F766E', with_courier: PURPLE, shipped: GREEN,
  closed: MUTED, return: RED,
}

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']

const axisStyle = { fontSize: 10, fill: MUTED, fontFamily: FONT_BODY }
const gridStyle = { strokeDasharray: '3 3', stroke: BORDER }

// Tint (light bg + border) for a given accent color — used for Insight boxes, badges, icon chips
const TINT = {
  [BLUE]:   { bg: 'var(--primary-light)', border: 'var(--primary-border)' },
  [GREEN]:  { bg: 'var(--success-light)', border: 'var(--success-border)' },
  [RED]:    { bg: 'var(--error-light)',   border: 'var(--error-border)' },
  [AMBER]:  { bg: 'var(--warning-light)', border: 'var(--warning-border)' },
  [PURPLE]: { bg: '#F5F3FF', border: '#DDD6FE' },
}
const getTint = (color) => TINT[color] || TINT[BLUE]

// ── Flat icons (replace emojis) ─────────────────────────────────────────────
const FLAT = {
  cash:     'M10 18a8 8 0 100-16 8 8 0 000 16zM8.5 7.5a1.5 1.5 0 113 0c0 .56-.31 1.05-.77 1.31l-.73.43V10h-1V9l1.27-.75c.15-.09.23-.25.23-.42a.5.5 0 10-1 0H8.5zm1.5 4a.75.75 0 100 1.5.75.75 0 000-1.5z',
  trendUp:  'M12.577 4.878a.75.75 0 01.919-.53l4.78 1.281a.75.75 0 01.531.918l-1.281 4.78a.75.75 0 01-1.449-.387l.81-3.022a19.407 19.407 0 00-5.594 5.203.75.75 0 01-1.139.093L7.5 10.5l-4.72 4.72a.75.75 0 01-1.06-1.06l5.25-5.25a.75.75 0 011.06 0l3.074 3.073a20.923 20.923 0 015.545-4.931l-3.042-.815a.75.75 0 01-.53-.919z',
  box:      'M2 3a1 1 0 00-1 1v2a1 1 0 001 1h16a1 1 0 001-1V4a1 1 0 00-1-1H2zM2 7.5h16l-.811 7.71a2 2 0 01-1.99 1.79H4.8a2 2 0 01-1.99-1.79L2 7.5zm6 2.25a.75.75 0 000 1.5h4a.75.75 0 000-1.5H8z',
  undo:     'M9.78 4.22a.75.75 0 010 1.06L7.06 8h7.19a4.75 4.75 0 010 9.5h-1.5a.75.75 0 010-1.5h1.5a3.25 3.25 0 000-6.5H7.06l2.72 2.72a.75.75 0 11-1.06 1.06l-4-4a.75.75 0 010-1.06l4-4a.75.75 0 011.06 0z',
  tag:      'M5.5 3A2.5 2.5 0 003 5.5v2.879a2.5 2.5 0 00.732 1.767l6.5 6.5a2.5 2.5 0 003.536 0l2.878-2.878a2.5 2.5 0 000-3.536l-6.5-6.5A2.5 2.5 0 008.379 3H5.5zM6 7a1 1 0 100-2 1 1 0 000 2z',
  clock:    'M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z',
  refresh:  'M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H3.989a.75.75 0 00-.75.75v4.242a.75.75 0 001.5 0v-2.43l.31.31a7 7 0 0011.712-3.138.75.75 0 00-1.449-.39zm1.23-3.723a.75.75 0 00.219-.53V2.929a.75.75 0 00-1.5 0v2.43l-.31-.31A7 7 0 002.739 8.187a.75.75 0 101.449.39 5.5 5.5 0 019.201-2.466l.312.311H11.27a.75.75 0 000 1.5h4.243a.75.75 0 00.53-.219z',
  calendar: 'M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z',
  bulb:     'M10 1a6 6 0 00-3.815 10.631c.25.26.315.579.315.869v1.25a.75.75 0 00.75.75h5.5a.75.75 0 00.75-.75V12.5c0-.29.066-.609.315-.869A6 6 0 0010 1zM8.5 16.5a.75.75 0 000 1.5h3a.75.75 0 000-1.5h-3z',
  warning:  'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
}

function FlatIcon({ path, size=15, color='var(--text-muted)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill={color}>
      <path fillRule="evenodd" d={path} clipRule="evenodd"/>
    </svg>
  )
}

function ChartTip({ active, payload, label, fmt }) {
  if (!active || !payload || !payload.length) return null
  return (
    <div style={{ background:WHITE, border:'1px solid '+BORDER, borderRadius:'var(--radius-md)', padding:'10px 14px', fontFamily:FONT_BODY, boxShadow:'var(--shadow-lg)' }}>
      <div style={{ fontSize:'12px', fontWeight:700, color:TEXT, marginBottom:'6px', fontFamily:FONT_UI }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize:'11.5px', color:p.color, marginBottom:'2px' }}>
          {p.name}: <strong>{['revenue','profit','cogs','avg','costs','spend'].includes(p.dataKey) ? fmt(p.value) : p.value}</strong>
        </div>
      ))}
    </div>
  )
}

// ── Animated count-up — turns numbers into a satisfying tick-up on mount ───
function useCountUp(target, duration=1100, decimals=0) {
  const [val, setVal] = useState(0)
  useEffect(() => {
    if (!target) { setVal(0); return }
    let raf
    const t0 = performance.now()
    const tick = (now) => {
      const p = Math.min((now-t0)/duration, 1)
      const e = 1-Math.pow(1-p,3)
      setVal(target*e)
      if (p<1) raf = requestAnimationFrame(tick)
      else setVal(target)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])
  return decimals>0 ? val.toFixed(decimals) : Math.round(val)
}

// ── Animated horizontal bar fill — grows from 0 to target % on mount ───────
function AnimatedBar({ pct, color, height='8px', radius='4px', delay=0 }) {
  const [w, setW] = useState(0)
  useEffect(() => {
    const id = setTimeout(() => setW(Math.max(0, Math.min(100, pct))), 60 + delay)
    return () => clearTimeout(id)
  }, [pct, delay])
  return (
    <div style={{ width:'100%', height, borderRadius:radius, background:'var(--bg-muted)', overflow:'hidden' }}>
      <div style={{ height:'100%', borderRadius:radius, width:w+'%', background:color, transition:'width 1.1s cubic-bezier(0.22,1,0.36,1)' }}/>
    </div>
  )
}

// ── KPI card — animated value + flat icon, used in both grid and carousel ──
function KpiCard({ label, value, decimals=0, suffix='', formatter, sub, color, trend, iconPath, index=0 }) {
  const counted = useCountUp(value, 1100, decimals)
  const display = formatter ? formatter(counted) : counted + suffix
  const tint = getTint(color)
  return (
    <div className="an-kpi-card" style={{ background:WHITE, borderRadius:'var(--radius-lg)', border:'1px solid '+BORDER, padding:'16px', position:'relative', overflow:'hidden', animationDelay:(index*55)+'ms' }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:'3px', background:color }} />
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'8px' }}>
        <div style={{ width:'30px', height:'30px', borderRadius:'var(--radius-md)', background:tint.bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <FlatIcon path={iconPath} size={15} color={color}/>
        </div>
        {trend !== undefined && trend !== null && (
          <div style={{ fontSize:'11px', fontWeight:700, color: trend>=0?GREEN:RED, background: trend>=0?getTint(GREEN).bg:getTint(RED).bg, padding:'2px 8px', borderRadius:'var(--radius-full)' }}>
            {trend>=0?'↑':'↓'}{Math.abs(Math.round(trend))}%
          </div>
        )}
      </div>
      <div style={{ fontSize:'22px', fontWeight:800, color, marginBottom:'2px', fontFamily:FONT_UI, letterSpacing:'-0.3px' }}>{display}</div>
      <div style={{ fontSize:'11.5px', color:MUTED, fontFamily:FONT_BODY }}>{label}</div>
      {sub && <div style={{ fontSize:'11px', color:MUTED, marginTop:'2px', fontFamily:FONT_BODY }}>{sub}</div>}
    </div>
  )
}

function Section({ title, subtitle, children }) {
  return (
    <div style={{ background:WHITE, borderRadius:'var(--radius-lg)', border:'1px solid '+BORDER, overflow:'hidden', marginBottom:'14px' }} className="an-fade-in">
      <div style={{ padding:'14px 16px', borderBottom:'1px solid '+BORDER, background:'var(--bg-muted)' }}>
        <div style={{ fontSize:'14px', fontWeight:700, color:TEXT, fontFamily:FONT_UI }}>{title}</div>
        {subtitle && <div style={{ fontSize:'11.5px', color:MUTED, marginTop:'2px', fontFamily:FONT_BODY }}>{subtitle}</div>}
      </div>
      <div style={{ padding:'16px' }}>{children}</div>
    </div>
  )
}

function Insight({ text, color = BLUE }) {
  const tint = getTint(color)
  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap:'8px', padding:'10px 14px', borderRadius:'var(--radius-md)', background:tint.bg, border:'1px solid '+tint.border, marginTop:'12px', fontSize:'12px', color:TEXT, lineHeight:1.7, fontFamily:FONT_BODY }}>
      <FlatIcon path={FLAT.bulb} size={15} color={color}/>
      <span>{text}</span>
    </div>
  )
}

function DayHourHeatmap({ data, t }) {
  if (!data || data.length === 0) return null
  const max  = Math.max(...data.map(d => d.count), 1)
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
  const hrs  = [0,3,6,9,12,15,18,21]
  return (
    <div>
      <div style={{ display: 'flex', gap: '2px', alignItems: 'center', marginBottom: '4px' }}>
        <div style={{ width: '28px' }} />
        {hrs.map(h => (
          <div key={h} style={{ flex: 1, textAlign: 'center', fontSize: '9px', color: MUTED, fontFamily:FONT_BODY }}>
            {h < 12 ? h + t('analytics.am') : h === 12 ? t('analytics.noon') : (h - 12) + t('analytics.pm')}
          </div>
        ))}
      </div>
      {days.map(day => (
        <div key={day} style={{ display: 'flex', gap: '2px', alignItems: 'center', marginBottom: '2px' }}>
          <div style={{ width: '28px', fontSize: '9px', color: MUTED, textAlign: 'right', paddingRight: '4px', fontFamily:FONT_BODY }}>{day}</div>
          {hrs.map(hr => {
            const cell      = data.find(d => d.day === day && d.hour === hr)
            const count     = cell?.count || 0
            const intensity = count / max
            const bg = count === 0 ? 'var(--bg-muted)'
              : intensity > 0.75 ? HEAT_BASE
              : intensity > 0.5  ? HEAT_BASE + 'CC'
              : intensity > 0.25 ? HEAT_BASE + '70'
              : HEAT_BASE + '30'
            return (
              <div key={hr} title={day + ' ' + (h => h < 12 ? h + 'am' : h === 12 ? '12pm' : (h-12)+'pm')(hr) + ': ' + count + ' orders'}
                className="an-fade-in"
                style={{ flex: 1, aspectRatio: '1', borderRadius: '3px', background: bg }} />
            )
          })}
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', justifyContent: 'flex-end' }}>
        <span style={{ fontSize: '10px', color: MUTED, fontFamily:FONT_BODY }}>{t('analytics.heatmap_low')}</span>
        {['var(--bg-muted)', HEAT_BASE+'30', HEAT_BASE+'70', HEAT_BASE+'CC', HEAT_BASE].map((c,i) => (
          <div key={i} style={{ width: '12px', height: '12px', borderRadius: '2px', background: c }} />
        ))}
        <span style={{ fontSize: '10px', color: MUTED, fontFamily:FONT_BODY }}>{t('analytics.heatmap_high')}</span>
      </div>
    </div>
  )
}

function Funnel({ steps }) {
  if (!steps || steps.length === 0) return null
  const max = steps[0].value || 1
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {steps.map((step, i) => {
        const pct = Math.round((step.value / max) * 100)
        return (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px', fontFamily:FONT_BODY }}>
              <span style={{ color: TEXT, fontWeight: 600 }}>{step.label}</span>
              <span style={{ color: MUTED }}>{step.value} <span style={{ color: step.color, fontWeight: 700 }}>({pct}%)</span></span>
            </div>
            <AnimatedBar pct={pct} color={step.color} height="10px" radius="5px" delay={i*100}/>
          </div>
        )
      })}
    </div>
  )
}

function Analytics() {
  const { seller, isFeatureAllowed } = useAuth()
  const navigate = useNavigate()
  const { t }    = useTranslation()

  const [period, setPeriod]       = useState('30')
  const [loading, setLoading]     = useState(true)
  const [activeTab, setActiveTab] = useState('revenue')

  const [dailyRevenue, setDailyRevenue]         = useState([])
  const [monthlyRevenue, setMonthlyRevenue]     = useState([])
  const [categoryRevenue, setCategoryRevenue]   = useState([])
  const [statusData, setStatusData]             = useState([])
  const [peakDays, setPeakDays]                 = useState([])
  const [dayHourData, setDayHourData]           = useState([])
  const [returnTrend, setReturnTrend]           = useState([])
  const [avgOrderTrend, setAvgOrderTrend]       = useState([])
  const [funnelData, setFunnelData]             = useState([])
  const [topByRevenue, setTopByRevenue]         = useState([])
  const [topByQuantity, setTopByQuantity]       = useState([])
  const [topByMargin, setTopByMargin]           = useState([])
  const [deadStock, setDeadStock]               = useState([])
  const [returnByProduct, setReturnByProduct]   = useState([])
  const [velocityData, setVelocityData]         = useState([])
  const [newVsReturning, setNewVsReturning]     = useState([])
  const [topCustomers, setTopCustomers]         = useState([])
  const [locationData, setLocationData]         = useState([])
  const [courierData, setCourierData]           = useState([])
  const [courierReturns, setCourierReturns]     = useState([])
  const [paymentMethods, setPaymentMethods]     = useState([])
  const [sourceData, setSourceData]             = useState([])
  const [outstandingAmount, setOutstandingAmount] = useState(0)
  const [eventAnalytics, setEventAnalytics]     = useState([])
  const [stats, setStats] = useState({
    totalRevenue: 0, totalOrders: 0, avgOrderValue: 0, returnRate: 0,
    grossProfit: 0, profitMargin: 0, confirmedOrders: 0, pendingOrders: 0,
    totalCOGS: 0, newCustomers: 0, completionRate: 0, retentionRate: 0, avgRepeatDays: 0,
  })
  const [prevStats, setPrevStats] = useState({ totalRevenue: 0, totalOrders: 0, avgOrderValue: 0 })

  const sym = seller?.seller_currency === 'AUD' ? '$' : '৳'
  const fmt = useCallback((n) => {
    const abs = Math.abs(Math.round(n || 0))
    if (abs >= 1000000) return sym + (abs/1000000).toFixed(1) + 'M'
    if (abs >= 100000)  return sym + Math.round(abs/1000) + 'k'
    return sym + abs.toLocaleString()
  }, [sym])

  useEffect(() => {
    if (seller?.id && isFeatureAllowed('analytics_basic')) fetchAnalytics()
  }, [seller, period]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnalytics = async () => {
    setLoading(true)
    const daysAgo = new Date(); daysAgo.setDate(daysAgo.getDate() - Number(period))
    const prevPeriodStart = new Date(daysAgo); prevPeriodStart.setDate(prevPeriodStart.getDate() - Number(period))

    const [ordersRes, allOrdersRes, productsRes, customersRes, eventsRes] = await Promise.all([
      supabase.from('order_analytics').select('*').eq('seller_id', seller.id).gte('created_at', daysAgo.toISOString()).order('created_at', { ascending: true }),
      supabase.from('order_analytics').select('*').eq('seller_id', seller.id).order('created_at', { ascending: true }),
      supabase.from('products').select('*').eq('seller_id', seller.id),
      supabase.from('customers').select('id, created_at').eq('seller_id', seller.id),
      supabase.from('seller_events').select('*').eq('seller_id', seller.id).order('start_date', { ascending: false }),
    ])

    const orders    = ordersRes.data || []
    const allOrders = allOrdersRes.data || []
    const products  = productsRes.data || []

    const prevOrders = allOrders.filter(o => { const d = new Date(o.created_at); return d >= prevPeriodStart && d < daysAgo })
    const prevRev    = prevOrders.filter(o => o.status !== 'return').reduce((s, o) => s + Number(o.total_amount||0), 0)
    const prevOrd    = prevOrders.length
    setPrevStats({ totalRevenue: prevRev, totalOrders: prevOrd, avgOrderValue: prevOrd > 0 ? prevRev / prevOrd : 0 })

    const activeOrders = orders.filter(o => o.status !== 'return')
    const revenue = activeOrders.reduce((s, o) => s + Number(o.total_amount||0), 0)
    const cogs    = activeOrders.reduce((s, o) => s + (Number(o.product_buy_price||o.buy_price||0) * Number(o.quantity||1)), 0)
    const returns = orders.filter(o => o.status === 'return').length
    const confirmed   = orders.filter(o => ['confirmed','processing','with_courier','shipped','closed'].includes(o.status)).length
    const outstanding = orders.filter(o => o.payment_status === 'Unpaid COD' && !['return','closed'].includes(o.status)).reduce((s, o) => s + Number(o.total_amount||0), 0)
    setOutstandingAmount(outstanding)

    const customerOrderMap = {}
    allOrders.forEach(o => { if (!o.customer_id) return; if (!customerOrderMap[o.customer_id]) customerOrderMap[o.customer_id] = []; customerOrderMap[o.customer_id].push(new Date(o.created_at)) })
    const multiOrderCustomers = Object.values(customerOrderMap).filter(dates => dates.length > 1).length
    const totalCustomers = Object.keys(customerOrderMap).length
    const retentionRate = totalCustomers > 0 ? Math.round((multiOrderCustomers / totalCustomers) * 100) : 0

    let totalDays = 0; let pairCount = 0
    Object.values(customerOrderMap).forEach(dates => {
      if (dates.length < 2) return
      dates.sort((a,b) => a - b)
      for (let i = 1; i < dates.length; i++) { totalDays += (dates[i] - dates[i-1]) / 86400000; pairCount++ }
    })

    setStats({
      totalRevenue: revenue, totalOrders: orders.length,
      avgOrderValue: orders.length > 0 ? revenue / orders.length : 0,
      returnRate: orders.length > 0 ? (returns / orders.length) * 100 : 0,
      grossProfit: revenue - cogs, profitMargin: revenue > 0 ? ((revenue - cogs) / revenue) * 100 : 0,
      confirmedOrders: confirmed, pendingOrders: orders.filter(o => ['received','pending'].includes(o.status)).length,
      totalCOGS: cogs, completionRate: orders.length > 0 ? (confirmed / orders.length) * 100 : 0,
      newCustomers: (customersRes.data||[]).filter(c => new Date(c.created_at) >= daysAgo).length,
      retentionRate, avgRepeatDays: pairCount > 0 ? Math.round(totalDays / pairCount) : 0,
    })

    // Daily
    const dailyMap = {}
    orders.forEach(o => {
      const date = new Date(o.created_at).toLocaleDateString('en-BD', { day:'numeric', month:'short' })
      if (!dailyMap[date]) dailyMap[date] = { date, revenue: 0, orders: 0, profit: 0 }
      if (o.status !== 'return') { dailyMap[date].revenue += Number(o.total_amount||0); dailyMap[date].profit += Number(o.total_amount||0) - (Number(o.product_buy_price||0) * Number(o.quantity||1)) }
      dailyMap[date].orders++
    })
    setDailyRevenue(Object.values(dailyMap).slice(-14))

    // Monthly
    const monthlyMap = {}
    allOrders.forEach(o => {
      const key = new Date(o.created_at).toLocaleDateString('en-BD', { month:'short', year:'2-digit' })
      if (!monthlyMap[key]) monthlyMap[key] = { month: key, revenue: 0, profit: 0, orders: 0 }
      if (o.status !== 'return') { const rev = Number(o.total_amount||0); const cg = Number(o.product_buy_price||0) * Number(o.quantity||1); monthlyMap[key].revenue += rev; monthlyMap[key].profit += rev - cg; monthlyMap[key].orders++ }
    })
    const monthlyArr = Object.values(monthlyMap).slice(-12)
    setMonthlyRevenue(monthlyArr)
    setAvgOrderTrend(monthlyArr.map(d => ({ month: d.month, avg: d.orders > 0 ? Math.round(d.revenue / d.orders) : 0 })))

    // Category
    const catMap = {}
    orders.forEach(o => { const cat = o.product_category || 'Uncategorized'; if (!catMap[cat]) catMap[cat] = { category: cat, revenue: 0, orders: 0 }; if (o.status !== 'return') { catMap[cat].revenue += Number(o.total_amount||0); catMap[cat].orders++ } })
    setCategoryRevenue(Object.values(catMap).sort((a,b) => b.revenue - a.revenue).slice(0,8))

    // Status & funnel
    const statusMap = {}
    orders.forEach(o => { statusMap[o.status] = (statusMap[o.status]||0) + 1 })
    setStatusData(Object.entries(statusMap).map(([name, value]) => ({ name, value })))
    const total = orders.length
    setFunnelData([
      { label: t('analytics.funnel_received'), value: total, color: BLUE },
      { label: t('analytics.funnel_confirmed'), value: orders.filter(o => ['confirmed','processing','with_courier','shipped','closed'].includes(o.status)).length, color: PURPLE },
      { label: t('analytics.funnel_shipped'), value: orders.filter(o => ['with_courier','shipped','closed'].includes(o.status)).length, color: AMBER },
      { label: t('analytics.funnel_completed'), value: orders.filter(o => o.status === 'closed').length, color: GREEN },
    ])

    // Peak days
    const dayMap = Array(7).fill(0).map((_, i) => ({ day: DAYS[i], orders: 0 }))
    orders.forEach(o => { dayMap[new Date(o.created_at).getDay()].orders++ })
    setPeakDays(dayMap)

    // Day×Hour heatmap
    const dhMap = {}
    orders.forEach(o => { const d = new Date(o.created_at); const dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]; const hr = Math.floor(d.getHours() / 3) * 3; const key = dayName + '-' + hr; if (!dhMap[key]) dhMap[key] = { day: dayName, hour: hr, count: 0 }; dhMap[key].count++ })
    setDayHourData(Object.values(dhMap))

    // Return trend
    const retMap = {}
    allOrders.forEach(o => { const key = new Date(o.created_at).toLocaleDateString('en-BD', { month:'short', year:'2-digit' }); if (!retMap[key]) retMap[key] = { month: key, returns: 0, total: 0 }; retMap[key].total++; if (o.status === 'return') retMap[key].returns++ })
    setReturnTrend(Object.values(retMap).slice(-12).map(d => ({ ...d, rate: d.total > 0 ? Math.round((d.returns/d.total)*100) : 0 })))

    // Products
    const prodMap = {}
    orders.forEach(o => { const key = o.product_name || o.product_code || 'Unknown'; if (!prodMap[key]) prodMap[key] = { name: key, revenue: 0, quantity: 0, returns: 0 }; if (o.status !== 'return') { prodMap[key].revenue += Number(o.total_amount||0); prodMap[key].quantity += Number(o.quantity||1) }; if (o.status === 'return') prodMap[key].returns++ })
    const prodArr = Object.values(prodMap)
    setTopByRevenue([...prodArr].sort((a,b) => b.revenue - a.revenue).slice(0,8))
    setTopByQuantity([...prodArr].sort((a,b) => b.quantity - a.quantity).slice(0,8))
    setReturnByProduct(prodArr.filter(p => p.returns > 0).sort((a,b) => b.returns - a.returns).slice(0,6))
    const periodDays = Number(period)
    setVelocityData([...prodArr].sort((a,b) => b.quantity - a.quantity).slice(0,8).map(p => ({ name: p.name, velocity: parseFloat((p.quantity / periodDays).toFixed(2)), quantity: p.quantity })))

    const marginArr = products.filter(p => p.sell_price && p.buy_price).map(p => ({ name: p.name, margin: Math.round(((p.sell_price - p.buy_price) / p.sell_price) * 100), profit: p.sell_price - p.buy_price })).sort((a,b) => b.margin - a.margin).slice(0,8)
    setTopByMargin(marginArr)

    const thirtyDaysAgo = new Date(); thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    const recentProductIds = new Set(allOrders.filter(o => new Date(o.created_at) >= thirtyDaysAgo).map(o => o.product_id))
    setDeadStock(products.filter(p => p.stock > 0 && !recentProductIds.has(p.id)).map(p => ({ name: p.name, stock: p.stock, value: p.stock * (p.buy_price||0) })).slice(0,8))

    // Customers
    const custOrderCount = {}
    allOrders.forEach(o => { custOrderCount[o.customer_id] = (custOrderCount[o.customer_id]||0) + 1 })
    const periodCusts = new Set(orders.map(o => o.customer_id))
    let newC = 0; let retC = 0
    periodCusts.forEach(cid => { if (custOrderCount[cid] <= 1) newC++; else retC++ })
    setNewVsReturning([{ name: 'New', value: newC }, { name: 'Returning', value: retC }])

    const custSpend = {}
    orders.forEach(o => { if (!o.customer_id) return; if (!custSpend[o.customer_id]) custSpend[o.customer_id] = { name: 'Customer', spend: 0, orders: 0 }; custSpend[o.customer_id].orders++; if (o.status !== 'return') custSpend[o.customer_id].spend += Number(o.total_amount||0) })
    setTopCustomers(Object.values(custSpend).sort((a,b) => b.spend - a.spend).slice(0,10))

    const locMap = {}
    orders.forEach(o => { const loc = o.district || o.thana || 'Unknown'; locMap[loc] = (locMap[loc]||0) + 1 })
    setLocationData(Object.entries(locMap).sort((a,b) => b[1] - a[1]).slice(0,10).map(([name,value]) => ({ name, value })))

    const courierMap = {}
    orders.forEach(o => { const c = o.courier || 'Unknown'; if (!courierMap[c]) courierMap[c] = { name: c, orders: 0, returns: 0 }; courierMap[c].orders++; if (o.status === 'return') courierMap[c].returns++ })
    const cArr = Object.values(courierMap)
    setCourierData([...cArr].sort((a,b) => b.orders - a.orders))
    setCourierReturns(cArr.map(c => ({ ...c, returnRate: c.orders > 0 ? Math.round((c.returns/c.orders)*100) : 0 })).sort((a,b) => b.returnRate - a.returnRate))

    const pmMap = {}
    orders.forEach(o => { const pm = (o.payment_method||'Unknown').split(' · ')[0]; pmMap[pm] = (pmMap[pm]||0) + 1 })
    setPaymentMethods(Object.entries(pmMap).map(([name,value]) => ({ name, value })))

    const srcMap = {}
    orders.forEach(o => { const s = o.order_source||'Unknown'; srcMap[s] = (srcMap[s]||0) + 1 })
    setSourceData(Object.entries(srcMap).map(([name,value]) => ({ name, value })))

    if (eventsRes.data && eventsRes.data.length > 0) {
      const evStats = eventsRes.data.map(ev => {
        const evOrders = allOrders.filter(o => o.event_id === ev.id && o.status !== 'return')
        const evRev    = evOrders.reduce((s, o) => s + Number(o.total_amount||0), 0)
        const evCosts  = Number(ev.stall_fee||0) + Number(ev.transport_cost||0) + Number(ev.other_costs||0)
        const evProfit = evRev - evCosts
        return { name: ev.name, type: ev.event_type, date: ev.start_date, revenue: Math.round(evRev), costs: Math.round(evCosts), profit: Math.round(evProfit), orders: evOrders.length, roi: evCosts > 0 ? Math.round((evProfit/evCosts)*100) : 0 }
      }).filter(e => e.orders > 0 || e.costs > 0)
      setEventAnalytics(evStats)
    }

    setLoading(false)
  }

  const revTrend = prevStats.totalRevenue > 0 ? ((stats.totalRevenue - prevStats.totalRevenue) / prevStats.totalRevenue) * 100 : null
  const ordTrend = prevStats.totalOrders  > 0 ? ((stats.totalOrders  - prevStats.totalOrders)  / prevStats.totalOrders)  * 100 : null
  const avgTrend = prevStats.avgOrderValue > 0 ? ((stats.avgOrderValue - prevStats.avgOrderValue) / prevStats.avgOrderValue) * 100 : null

  const bestDay      = peakDays.length > 0 ? peakDays.reduce((a,b) => a.orders > b.orders ? a : b) : null
  const topProduct   = topByRevenue[0]
  const worstCourier = courierReturns[0]
  const bestEvent    = eventAnalytics.length > 0 ? eventAnalytics.reduce((a,b) => a.profit > b.profit ? a : b) : null
  const topSource    = sourceData.length > 0 ? [...sourceData].sort((a,b) => b.value - a.value)[0] : null

  // Funnel completion rate
  const funnelCompletionRate = funnelData[0]?.value > 0 ? Math.round(((funnelData[3]?.value || 0) / funnelData[0].value) * 100) : 0

  const TABS = [
    { key: 'revenue',   label: t('analytics.tab_revenue') },
    { key: 'orders',    label: t('analytics.tab_orders') },
    { key: 'products',  label: t('analytics.tab_products') },
    { key: 'customers', label: t('analytics.tab_customers') },
    { key: 'delivery',  label: t('analytics.tab_delivery') },
    ...(eventAnalytics.length > 0 ? [{ key: 'events', label: t('analytics.tab_events') }] : []),
  ]

  // KPI definitions — shared between desktop grid and mobile carousel
  const kpis = [
    { label:t('analytics.kpi_revenue'),         value:stats.totalRevenue, formatter:fmt, color:BLUE,   trend:revTrend, iconPath:FLAT.cash,     sub:t('analytics.kpi_vs_prev')+' '+period+'d' },
    { label:t('analytics.kpi_profit'),          value:stats.grossProfit,  formatter:fmt, color:GREEN,  trend:null,     iconPath:FLAT.trendUp,  sub:stats.profitMargin.toFixed(1)+'% '+t('analytics.kpi_margin') },
    { label:t('analytics.kpi_orders'),          value:stats.totalOrders,  formatter:null, color:PURPLE, trend:ordTrend, iconPath:FLAT.box,      sub:t('analytics.kpi_avg')+' '+fmt(stats.avgOrderValue) },
    { label:t('analytics.kpi_return_rate'),     value:stats.returnRate, decimals:1, suffix:'%', color:stats.returnRate>10?RED:GREEN, trend:null, iconPath:FLAT.undo, sub:stats.completionRate.toFixed(1)+'% '+t('analytics.kpi_completion') },
    { label:t('analytics.kpi_avg_order'),       value:stats.avgOrderValue, formatter:fmt, color:AMBER, trend:avgTrend, iconPath:FLAT.tag,      sub:t('analytics.kpi_vs_prev')+' '+period+'d' },
    { label:t('analytics.kpi_outstanding'),     value:outstandingAmount,  formatter:fmt, color:outstandingAmount>0?AMBER:GREEN, trend:null, iconPath:FLAT.clock, sub:t('analytics.kpi_unpaid_transit') },
    { label:t('analytics.kpi_retention'),       value:stats.retentionRate, suffix:'%', color:stats.retentionRate>30?GREEN:AMBER, trend:null, iconPath:FLAT.refresh, sub:t('analytics.kpi_repeat_buyers') },
    { label:t('analytics.kpi_repeat_interval'), value:stats.avgRepeatDays, suffix:stats.avgRepeatDays>0?' days':'', color:BLUE, trend:null, iconPath:FLAT.calendar, sub:t('analytics.kpi_avg_days') },
  ]

  if (!isFeatureAllowed('analytics_basic')) {
    return (
      <div className="pc-page" style={{ flex:1, background:BG, fontFamily:FONT_BODY }}>
        <Topbar title={t('analytics.title')} />
        <div style={{ padding:'48px 24px', maxWidth:'480px', margin:'0 auto', textAlign:'center' }}>
          <div style={{ background:WHITE, borderRadius:'var(--radius-lg)', padding:'40px', border:'1px solid '+BORDER }}>
            <div style={{ width:'64px', height:'64px', borderRadius:'var(--radius-lg)', background:getTint(BLUE).bg, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
              <FlatIcon path={FLAT.trendUp} size={28} color={BLUE}/>
            </div>
            <div style={{ fontSize:'16px', fontWeight:700, color:TEXT, marginBottom:'8px', fontFamily:FONT_UI }}>{t('analytics.upgrade_title')}</div>
            <div style={{ fontSize:'13px', color:MUTED, marginBottom:'24px', lineHeight:1.6, fontFamily:FONT_BODY }}>{t('analytics.upgrade_desc')}</div>
            <button onClick={() => navigate('/pricing')}
              style={{ width:'100%', padding:'13px', borderRadius:'var(--radius-md)', background:BLUE, border:'none', fontSize:'14px', fontWeight:700, color:WHITE, cursor:'pointer', fontFamily:FONT_UI }}>
              {t('analytics.upgrade_btn')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="pc-page" style={{ flex:1, overflowX:'hidden', background:BG, fontFamily:FONT_BODY }}>
      <Topbar title={t('analytics.title')} />
      <div style={{ padding:'18px 24px' }}>

        {/* Header + period */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px', flexWrap:'wrap', gap:'10px' }}>
          <div>
            <div style={{ fontSize:'20px', fontWeight:800, color:TEXT, fontFamily:FONT_UI, letterSpacing:'-0.3px' }}>{t('analytics.title')}</div>
            <div style={{ fontSize:'12px', color:MUTED, marginTop:'2px', fontFamily:FONT_BODY }}>{t('analytics.subtitle')}</div>
          </div>
          <div style={{ display:'flex', gap:'6px', padding:'4px', borderRadius:'var(--radius-md)', background:WHITE, border:'1px solid '+BORDER }}>
            {[{ k:'7',l:'7d' }, { k:'30',l:'30d' }, { k:'90',l:'3m' }, { k:'365',l:'1y' }].map(p => (
              <button key={p.k} onClick={() => setPeriod(p.k)}
                style={{ padding:'6px 14px', borderRadius:'var(--radius-sm)', border:'none', fontSize:'12.5px', fontWeight:600, cursor:'pointer', background:period===p.k?BLUE:'transparent', color:period===p.k?WHITE:MUTED, transition:'var(--transition-fast)', fontFamily:FONT_UI }}>
                {p.l}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ background:WHITE, borderRadius:'var(--radius-lg)', border:'1px solid '+BORDER, padding:'48px', textAlign:'center' }}>
            <div style={{ width:'24px', height:'24px', borderRadius:'50%', border:'2.5px solid '+BLUE, borderTopColor:'transparent', animation:'an-spin 0.7s linear infinite', margin:'0 auto 12px' }} />
            <div style={{ fontSize:'13px', color:MUTED, fontFamily:FONT_BODY }}>{t('analytics.loading')}</div>
          </div>
        ) : (
          <>
            {/* KPI cards — grid on desktop, swipeable carousel on mobile */}
            <div className="an-kpi-grid">
              {kpis.map((k,i) => (
                <KpiCard key={i} index={i} label={k.label} value={k.value} decimals={k.decimals} suffix={k.suffix} formatter={k.formatter} sub={k.sub} color={k.color} trend={k.trend} iconPath={k.iconPath}/>
              ))}
            </div>

            {/* Tabs */}
            <div className="an-tabs" style={{ display:'flex', gap:'4px', padding:'4px', borderRadius:'var(--radius-lg)', background:WHITE, border:'1px solid '+BORDER, marginBottom:'16px', overflowX:'auto' }}>
              {TABS.map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  style={{ padding:'8px 16px', borderRadius:'var(--radius-md)', border:'none', fontSize:'12.5px', fontWeight:600, cursor:'pointer', background:activeTab===tab.key?BLUE:'transparent', color:activeTab===tab.key?WHITE:MUTED, transition:'var(--transition-fast)', whiteSpace:'nowrap', flexShrink:0, fontFamily:FONT_UI }}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── REVENUE TAB ── */}
            {activeTab === 'revenue' && (
              <div>
                <Section title={t('analytics.rev_daily_title')} subtitle={t('analytics.rev_daily_sub')}>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={dailyRevenue} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={HEAT_BASE} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={HEAT_BASE} stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id="profGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#16A34A" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#16A34A" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid {...gridStyle} />
                      <XAxis dataKey="date" tick={axisStyle} tickLine={false} />
                      <YAxis tick={axisStyle} tickLine={false} axisLine={false} tickFormatter={v => v >= 1000 ? Math.round(v/1000)+'k' : v} />
                      <Tooltip content={<ChartTip fmt={fmt} />} />
                      <Area type="monotone" dataKey="revenue" name="revenue" stroke={BLUE} strokeWidth={2.5} fill="url(#revGrad)" dot={false} isAnimationActive animationDuration={1300} animationEasing="ease-out" />
                      <Area type="monotone" dataKey="profit" name="profit" stroke={GREEN} strokeWidth={2} fill="url(#profGrad)" dot={false} isAnimationActive animationDuration={1300} animationEasing="ease-out" animationBegin={150} />
                    </AreaChart>
                  </ResponsiveContainer>
                  <Insight text={
                    dailyRevenue.length > 1
                      ? (dailyRevenue[dailyRevenue.length-1].revenue > dailyRevenue[0].revenue
                          ? t('analytics.insight_avg_rising')
                          : t('analytics.insight_avg_flat'))
                      + (stats.profitMargin > 30 ? ' ' + t('analytics.insight_margin_good') : ' ' + t('analytics.insight_margin_low'))
                      : t('analytics.insight_margin_good')
                  } />
                </Section>

                <Section title={t('analytics.rev_monthly_title')} subtitle={t('analytics.rev_monthly_sub')}>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={monthlyRevenue} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                      <CartesianGrid {...gridStyle} />
                      <XAxis dataKey="month" tick={axisStyle} tickLine={false} />
                      <YAxis tick={axisStyle} tickLine={false} axisLine={false} tickFormatter={v => v >= 1000 ? Math.round(v/1000)+'k' : v} />
                      <Tooltip content={<ChartTip fmt={fmt} />} />
                      <Bar dataKey="revenue" name="revenue" fill={BLUE} radius={[4,4,0,0]} isAnimationActive animationDuration={1000} animationEasing="ease-out" />
                      <Bar dataKey="profit" name="profit" fill={GREEN} radius={[4,4,0,0]} isAnimationActive animationDuration={1000} animationEasing="ease-out" animationBegin={120} />
                    </BarChart>
                  </ResponsiveContainer>
                  <Insight text={
                    topProduct
                      ? t('analytics.insight_top_earner') + ' "' + topProduct.name + '" — ' + fmt(topProduct.revenue) + '. ' + (stats.profitMargin > 30 ? t('analytics.insight_margin_good') : t('analytics.insight_margin_low'))
                      : (stats.profitMargin > 30 ? t('analytics.insight_margin_good') : t('analytics.insight_margin_low'))
                  } />
                </Section>

                {categoryRevenue.length > 0 && (
                  <Section title={t('analytics.rev_category_title')}>
                    <ResponsiveContainer width="100%" height={Math.max(160, categoryRevenue.length * 32)}>
                      <BarChart data={categoryRevenue} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid {...gridStyle} horizontal={false} />
                        <XAxis type="number" tick={axisStyle} tickLine={false} tickFormatter={v => v >= 1000 ? Math.round(v/1000)+'k' : v} />
                        <YAxis type="category" dataKey="category" width={110} tick={axisStyle} tickLine={false} />
                        <Tooltip content={<ChartTip fmt={fmt} />} />
                        <Bar dataKey="revenue" name="revenue" radius={[0,4,4,0]} isAnimationActive animationDuration={1100} animationEasing="ease-out">
                          {categoryRevenue.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <Insight text={
                      categoryRevenue[0]
                        ? t('analytics.insight_top_earner') + ' "' + categoryRevenue[0].category + '" — ' + fmt(categoryRevenue[0].revenue) + '. ' + (categoryRevenue.length > 3 ? 'Revenue is spread across ' + categoryRevenue.length + ' categories.' : 'Consider expanding into more product categories to diversify revenue.')
                        : t('analytics.insight_margin_good')
                    } />
                  </Section>
                )}
              </div>
            )}

            {/* ── ORDERS TAB ── */}
            {activeTab === 'orders' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }} className="an-half-grid">
                  <Section title={t('analytics.ord_funnel_title')} subtitle={t('analytics.ord_funnel_sub')}>
                    <Funnel steps={funnelData} />
                    <Insight text={
                      funnelCompletionRate > 50
                        ? t('analytics.insight_funnel_good')
                        : t('analytics.insight_funnel_low')
                    } />
                  </Section>

                  {statusData.length > 0 && (
                    <Section title={t('analytics.ord_status_title')}>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={statusData} cx="50%" cy="45%" outerRadius={75} dataKey="value" nameKey="name"
                            label={({ percent }) => Math.round(percent*100)+'%'} labelLine={false} style={{ fontSize: '9px' }}
                            isAnimationActive animationDuration={1100} animationEasing="ease-out" animationBegin={100}>
                            {statusData.map((entry, i) => <Cell key={i} fill={STATUS_COLORS[entry.name] || PALETTE[i % PALETTE.length]} />)}
                          </Pie>
                          <Tooltip /><Legend iconSize={8} wrapperStyle={{ fontSize: '10px', fontFamily: FONT_BODY }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <Insight text={
                        stats.pendingOrders > stats.confirmedOrders
                          ? t('analytics.insight_status_pending')
                          : t('analytics.insight_status_ok')
                      } />
                    </Section>
                  )}
                </div>

                <Section title={t('analytics.ord_peak_title')} subtitle={t('analytics.ord_peak_sub')}>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={peakDays} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                      <CartesianGrid {...gridStyle} />
                      <XAxis dataKey="day" tick={axisStyle} tickLine={false} />
                      <YAxis tick={axisStyle} tickLine={false} axisLine={false} />
                      <Tooltip content={<ChartTip fmt={fmt} />} />
                      <Bar dataKey="orders" name="orders" radius={[6,6,0,0]} isAnimationActive animationDuration={1000} animationEasing="ease-out">
                        {peakDays.map((entry, i) => <Cell key={i} fill={entry.orders === Math.max(...peakDays.map(d => d.orders)) ? BLUE : BLUE+'40'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {bestDay && <Insight text={t('analytics.insight_busiest_day') + ' ' + bestDay.day + ' ' + t('analytics.insight_busiest_day2') + ' ' + bestDay.orders + ' ' + t('analytics.insight_busiest_day3')} />}
                </Section>

                <Section title={t('analytics.ord_heatmap_title')} subtitle={t('analytics.ord_heatmap_sub')}>
                  <DayHourHeatmap data={dayHourData} t={t} />
                  <Insight text={t('analytics.insight_heatmap')} />
                </Section>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }} className="an-half-grid">
                  <Section title={t('analytics.ord_avg_trend_title')}>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={avgOrderTrend} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                        <CartesianGrid {...gridStyle} />
                        <XAxis dataKey="month" tick={axisStyle} tickLine={false} />
                        <YAxis tick={axisStyle} tickLine={false} axisLine={false} tickFormatter={v => v >= 1000 ? Math.round(v/1000)+'k' : v} />
                        <Tooltip content={<ChartTip fmt={fmt} />} />
                        <Line type="monotone" dataKey="avg" name="avg" stroke={BLUE} strokeWidth={2.5} dot={{ r: 4, fill: BLUE }} isAnimationActive animationDuration={1300} animationEasing="ease-out" />
                      </LineChart>
                    </ResponsiveContainer>
                    <Insight text={
                      avgOrderTrend.length > 1 && avgOrderTrend[avgOrderTrend.length-1].avg > avgOrderTrend[0].avg
                        ? t('analytics.insight_avg_rising')
                        : t('analytics.insight_avg_flat')
                    } />
                  </Section>

                  <Section title={t('analytics.ord_return_trend_title')}>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={returnTrend} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                        <CartesianGrid {...gridStyle} />
                        <XAxis dataKey="month" tick={axisStyle} tickLine={false} />
                        <YAxis tick={axisStyle} tickLine={false} axisLine={false} />
                        <Tooltip content={<ChartTip fmt={fmt} />} />
                        <Line type="monotone" dataKey="rate" name="return rate %" stroke={RED} strokeWidth={2.5} dot={{ r: 4, fill: RED }} isAnimationActive animationDuration={1300} animationEasing="ease-out" />
                      </LineChart>
                    </ResponsiveContainer>
                    <Insight text={
                      stats.returnRate > 10
                        ? t('analytics.insight_return_high')
                        : t('analytics.insight_return_ok')
                    } color={stats.returnRate > 10 ? RED : GREEN} />
                  </Section>
                </div>
              </div>
            )}

            {/* ── PRODUCTS TAB ── */}
            {activeTab === 'products' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }} className="an-half-grid">
                  {topByRevenue.length > 0 && (
                    <Section title={t('analytics.prod_revenue_title')}>
                      <ResponsiveContainer width="100%" height={Math.max(160, topByRevenue.length * 28)}>
                        <BarChart data={topByRevenue} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid {...gridStyle} horizontal={false} />
                          <XAxis type="number" tick={axisStyle} tickLine={false} tickFormatter={v => v >= 1000 ? Math.round(v/1000)+'k' : v} />
                          <YAxis type="category" dataKey="name" width={100} tick={axisStyle} tickLine={false} />
                          <Tooltip content={<ChartTip fmt={fmt} />} />
                          <Bar dataKey="revenue" name="revenue" fill={BLUE} radius={[0,4,4,0]} isAnimationActive animationDuration={1100} animationEasing="ease-out" />
                        </BarChart>
                      </ResponsiveContainer>
                      <Insight text={
                        topByRevenue[0]
                          ? t('analytics.insight_top_earner') + ' "' + topByRevenue[0].name + '" — ' + fmt(topByRevenue[0].revenue) + '. ' + t('analytics.insight_focus_margin')
                          : t('analytics.insight_focus_margin')
                      } />
                    </Section>
                  )}

                  {topByQuantity.length > 0 && (
                    <Section title={t('analytics.prod_units_title')}>
                      <ResponsiveContainer width="100%" height={Math.max(160, topByQuantity.length * 28)}>
                        <BarChart data={topByQuantity} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid {...gridStyle} horizontal={false} />
                          <XAxis type="number" tick={axisStyle} tickLine={false} />
                          <YAxis type="category" dataKey="name" width={100} tick={axisStyle} tickLine={false} />
                          <Tooltip content={<ChartTip fmt={fmt} />} />
                          <Bar dataKey="quantity" name="units" fill={GREEN} radius={[0,4,4,0]} isAnimationActive animationDuration={1100} animationEasing="ease-out" animationBegin={100} />
                        </BarChart>
                      </ResponsiveContainer>
                      <Insight text={t('analytics.insight_units_vs_rev')} />
                    </Section>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }} className="an-half-grid">
                  {velocityData.length > 0 && (
                    <Section title={t('analytics.prod_velocity_title')} subtitle={t('analytics.prod_velocity_sub')}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {velocityData.map((p, i) => (
                          <div key={i}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px', fontFamily:FONT_BODY }}>
                              <span style={{ color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>{p.name}</span>
                              <span style={{ color: BLUE, fontWeight: 700, flexShrink: 0, marginLeft: '6px' }}>{p.velocity}/day</span>
                            </div>
                            <AnimatedBar pct={Math.round((p.velocity / velocityData[0].velocity) * 100)} color={BLUE} height="6px" radius="3px" delay={i*70}/>
                          </div>
                        ))}
                      </div>
                      <Insight text={
                        velocityData[0]
                          ? t('analytics.insight_velocity_fast') + ' ' + velocityData[0].velocity + ' ' + t('analytics.insight_velocity_day')
                          : t('analytics.insight_focus_margin')
                      } />
                    </Section>
                  )}

                  {topByMargin.length > 0 && (
                    <Section title={t('analytics.prod_margin_title')} subtitle={t('analytics.prod_margin_sub')}>
                      <ResponsiveContainer width="100%" height={Math.max(160, topByMargin.length * 28)}>
                        <BarChart data={topByMargin} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid {...gridStyle} horizontal={false} />
                          <XAxis type="number" tick={axisStyle} tickLine={false} />
                          <YAxis type="category" dataKey="name" width={100} tick={axisStyle} tickLine={false} />
                          <Tooltip content={<ChartTip fmt={fmt} />} />
                          <Bar dataKey="margin" name="margin %" fill={PURPLE} radius={[0,4,4,0]} isAnimationActive animationDuration={1100} animationEasing="ease-out" animationBegin={100} />
                        </BarChart>
                      </ResponsiveContainer>
                      <Insight text={
                        topByMargin[0]
                          ? '"' + topByMargin[0].name + '" ' + t('analytics.insight_top_margin') + ' ' + topByMargin[0].margin + '%. ' + t('analytics.insight_focus_margin')
                          : t('analytics.insight_focus_margin')
                      } color={PURPLE} />
                    </Section>
                  )}
                </div>

                {returnByProduct.length > 0 && (
                  <Section title={t('analytics.prod_returns_title')} subtitle={t('analytics.prod_returns_sub')}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {returnByProduct.map((p, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ fontSize: '12px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: TEXT, fontFamily:FONT_BODY }}>{p.name}</div>
                          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '80px' }}>
                              <AnimatedBar pct={Math.min(100, (p.returns / (p.quantity + p.returns)) * 100)} color={RED} height="6px" radius="3px" delay={i*70}/>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: RED, minWidth: '40px', fontFamily:FONT_BODY }}>{p.returns} ret</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Insight text={
                      returnByProduct[0]
                        ? '"' + returnByProduct[0].name + '" ' + t('analytics.insight_returns_product')
                        : t('analytics.insight_return_ok')
                    } color={RED} />
                  </Section>
                )}

                {deadStock.length > 0 && (
                  <Section title={t('analytics.prod_dead_title')} subtitle={t('analytics.prod_dead_sub')}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {deadStock.map((p, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: getTint(AMBER).bg, border: '1px solid '+getTint(AMBER).border, flexWrap:'wrap', gap:'6px' }}>
                          <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, marginRight: '10px', fontFamily:FONT_BODY }}>{p.name}</div>
                          <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
                            <span style={{ fontSize: '12px', color: AMBER, fontFamily:FONT_BODY }}>{p.stock} {t('analytics.prod_dead_units')}</span>
                            {p.value > 0 && <span style={{ fontSize: '12px', fontWeight: 700, color: RED, fontFamily:FONT_BODY }}>{fmt(p.value)} {t('analytics.prod_dead_tied')}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <Insight text={deadStock.length + ' ' + t('analytics.insight_dead_stock')} color={AMBER} />
                  </Section>
                )}
              </div>
            )}

            {/* ── CUSTOMERS TAB ── */}
            {activeTab === 'customers' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }} className="an-half-grid">
                  {newVsReturning.some(d => d.value > 0) && (
                    <Section title={t('analytics.cust_new_returning_title')}>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie data={newVsReturning} cx="50%" cy="45%" innerRadius={45} outerRadius={70} paddingAngle={4}
                            dataKey="value" nameKey="name" label={({ percent }) => Math.round(percent*100)+'%'} labelLine={false} style={{ fontSize: '9px' }}
                            isAnimationActive animationDuration={1100} animationEasing="ease-out" animationBegin={100}>
                            {newVsReturning.map((_, i) => <Cell key={i} fill={[BLUE, GREEN][i]} />)}
                          </Pie>
                          <Tooltip /><Legend iconSize={8} wrapperStyle={{ fontSize: '10px', fontFamily:FONT_BODY }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                        {[
                          { label: t('analytics.cust_retention'), value: stats.retentionRate + '%', color: stats.retentionRate > 30 ? GREEN : AMBER },
                          { label: t('analytics.cust_repeat_interval'), value: stats.avgRepeatDays > 0 ? stats.avgRepeatDays + 'd' : '—', color: BLUE },
                        ].map((s, i) => (
                          <div key={i} style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', background: 'var(--bg-muted)', textAlign: 'center' }}>
                            <div style={{ fontSize: '18px', fontWeight: 700, color: s.color, fontFamily:FONT_UI }}>{s.value}</div>
                            <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px', fontFamily:FONT_BODY }}>{s.label}</div>
                          </div>
                        ))}
                      </div>
                      <Insight text={
                        (newVsReturning[1]?.value > 0
                          ? newVsReturning[1].value + ' ' + t('analytics.insight_returning') + ' ' + stats.retentionRate + '%. '
                          : t('analytics.insight_new_only') + ' ')
                        + (stats.avgRepeatDays > 0
                          ? t('analytics.insight_repeat_days') + ' ' + stats.avgRepeatDays + ' ' + t('analytics.insight_repeat_days2')
                          : '')
                      } />
                    </Section>
                  )}

                  {locationData.length > 0 && (
                    <Section title={t('analytics.cust_location_title')} subtitle={t('analytics.cust_location_sub')}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {locationData.map((loc, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ fontSize: '12px', minWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: TEXT, fontFamily:FONT_BODY }}>{loc.name}</div>
                            <div style={{ flex: 1 }}>
                              <AnimatedBar pct={Math.round((loc.value / locationData[0].value) * 100)} color={PALETTE[i % PALETTE.length]} height="8px" radius="4px" delay={i*60}/>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: TEXT, minWidth: '24px', textAlign: 'right', fontFamily:FONT_BODY }}>{loc.value}</span>
                          </div>
                        ))}
                      </div>
                      <Insight text={
                        locationData[0]
                          ? t('analytics.insight_most_orders') + ' ' + locationData[0].name + '. ' + (locationData.length > 3 ? 'Orders span ' + locationData.length + ' locations — great geographic spread.' : 'Consider targeted promotions in your top locations.')
                          : ''
                      } />
                    </Section>
                  )}
                </div>

                {topCustomers.length > 0 && (
                  <Section title={t('analytics.cust_top_title')}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {topCustomers.slice(0, 8).map((c, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: i === 0 ? getTint(BLUE).bg : 'var(--bg-muted)', border: '1px solid ' + (i === 0 ? getTint(BLUE).border : BORDER), flexWrap:'wrap', gap:'6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: getTint(PALETTE[i % PALETTE.length] === PURPLE ? PURPLE : BLUE).bg, border: '1.5px solid ' + PALETTE[i % PALETTE.length] + '40', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: PALETTE[i % PALETTE.length], flexShrink: 0, fontFamily:FONT_UI }}>{i + 1}</div>
                            <span style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT, fontFamily:FONT_BODY }}>{c.name}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexShrink: 0 }}>
                            <span style={{ fontSize: '12px', color: MUTED, fontFamily:FONT_BODY }}>{c.orders} {t('analytics.cust_orders')}</span>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: BLUE, fontFamily:FONT_UI }}>{fmt(c.spend)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Insight text={
                      topCustomers[0]
                        ? 'Your top customer has spent ' + fmt(topCustomers[0].spend) + ' across ' + topCustomers[0].orders + ' orders. Nurture your VIP customers with exclusive offers to keep them coming back.'
                        : ''
                    } color={GREEN} />
                  </Section>
                )}

                {sourceData.length > 0 && (
                  <Section title={t('analytics.cust_source_title')} subtitle={t('analytics.cust_source_sub')}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[...sourceData].sort((a,b) => b.value - a.value).map((s, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ fontSize: '12px', minWidth: '120px', color: TEXT, fontFamily:FONT_BODY }}>{s.name}</div>
                          <div style={{ flex: 1 }}>
                            <AnimatedBar pct={Math.round((s.value / Math.max(...sourceData.map(x => x.value))) * 100)} color={BLUE} height="8px" radius="4px" delay={i*60}/>
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: TEXT, minWidth: '24px', textAlign: 'right', fontFamily:FONT_BODY }}>{s.value}</span>
                        </div>
                      ))}
                    </div>
                    <Insight text={
                      topSource
                        ? t('analytics.insight_top_source') + ' ' + topSource.name + '. ' + t('analytics.insight_source_diversify')
                        : t('analytics.insight_source_diversify')
                    } />
                  </Section>
                )}
              </div>
            )}

            {/* ── DELIVERY TAB ── */}
            {activeTab === 'delivery' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }} className="an-half-grid">
                  {courierData.length > 0 && (
                    <Section title={t('analytics.del_courier_title')}>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie data={courierData} cx="50%" cy="45%" innerRadius={45} outerRadius={70} paddingAngle={4}
                            dataKey="orders" nameKey="name" label={({ percent }) => Math.round(percent*100)+'%'} labelLine={false} style={{ fontSize: '9px' }}
                            isAnimationActive animationDuration={1100} animationEasing="ease-out" animationBegin={100}>
                            {courierData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                          </Pie>
                          <Tooltip /><Legend iconSize={8} wrapperStyle={{ fontSize: '10px', fontFamily:FONT_BODY }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <Insight text={
                        courierData[0]
                          ? courierData[0].name + ' ' + t('analytics.insight_courier_most') + (courierData.length === 1 ? ' Consider spreading deliveries across multiple couriers for redundancy.' : '')
                          : t('analytics.insight_courier_ok')
                      } />
                    </Section>
                  )}

                  {paymentMethods.length > 0 && (
                    <Section title={t('analytics.del_payment_title')}>
                      <ResponsiveContainer width="100%" height={180}>
                        <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="90%" data={paymentMethods.map((m, i) => ({ ...m, fill: PALETTE[i % PALETTE.length] }))}>
                          <RadialBar dataKey="value" isAnimationActive animationDuration={1200} animationEasing="ease-out" />
                          <Legend iconSize={8} wrapperStyle={{ fontSize: '10px', fontFamily:FONT_BODY }} />
                          <Tooltip />
                        </RadialBarChart>
                      </ResponsiveContainer>
                      <Insight text={
                        paymentMethods.length > 0
                          ? (() => {
                              const sorted = [...paymentMethods].sort((a,b) => b.value - a.value)
                              const codPct = sorted[0]?.name?.toLowerCase().includes('cod') ? Math.round((sorted[0].value / paymentMethods.reduce((s,m) => s + m.value, 0)) * 100) : 0
                              return codPct > 80
                                ? t('analytics.insight_payment_cod_only')
                                : sorted[0].name + ' ' + t('analytics.insight_payment_top')
                            })()
                          : ''
                      } />
                    </Section>
                  )}
                </div>

                {courierReturns.length > 0 && (
                  <Section title={t('analytics.del_return_rate_title')} subtitle={t('analytics.del_return_rate_sub')}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {courierReturns.map((c, i) => {
                        const rColor = c.returnRate > 20 ? RED : c.returnRate > 10 ? AMBER : GREEN
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ fontSize: '12px', minWidth: '100px', color: TEXT, fontFamily:FONT_BODY }}>{c.name}</div>
                            <div style={{ flex: 1 }}>
                              <AnimatedBar pct={Math.min(100, c.returnRate * 2)} color={rColor} height="8px" radius="4px" delay={i*60}/>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 700, minWidth: '36px', textAlign: 'right', color: rColor, fontFamily:FONT_BODY }}>{c.returnRate}%</span>
                          </div>
                        )
                      })}
                    </div>
                    <Insight text={
                      worstCourier && worstCourier.returnRate > 15
                        ? worstCourier.name + ' ' + t('analytics.insight_courier_high_return') + ' ' + worstCourier.returnRate + '% ' + t('analytics.insight_courier_switch')
                        : t('analytics.insight_courier_ok')
                    } color={worstCourier && worstCourier.returnRate > 15 ? RED : GREEN} />
                  </Section>
                )}

                {outstandingAmount > 0 && (
                  <div style={{ padding: '16px', borderRadius: 'var(--radius-lg)', background: getTint(AMBER).bg, border: '1px solid '+getTint(AMBER).border, marginBottom: '14px' }} className="an-fade-in">
                    <div style={{ fontSize: '12px', fontWeight: 700, color: AMBER, marginBottom: '4px', fontFamily:FONT_UI }}>{t('analytics.del_outstanding_title')}</div>
                    <div style={{ fontSize: '28px', fontWeight: 800, color: AMBER, fontFamily:FONT_UI }}>{fmt(outstandingAmount)}</div>
                    <div style={{ fontSize: '12px', color: MUTED, marginTop: '3px', fontFamily:FONT_BODY }}>{t('analytics.del_outstanding_sub')}</div>
                    <Insight text={t('analytics.insight_cod_high')} color={AMBER} />
                  </div>
                )}

                {outstandingAmount === 0 && (
                  <Insight text={t('analytics.insight_cod_ok')} color={GREEN} />
                )}
              </div>
            )}

            {/* ── EVENTS TAB ── */}
            {activeTab === 'events' && eventAnalytics.length > 0 && (
              <div>
                <Section title={t('analytics.ev_rev_costs_title')}>
                  <ResponsiveContainer width="100%" height={Math.max(160, eventAnalytics.length * 40)}>
                    <BarChart data={eventAnalytics} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid {...gridStyle} horizontal={false} />
                      <XAxis type="number" tick={axisStyle} tickLine={false} tickFormatter={v => v >= 1000 ? Math.round(v/1000)+'k' : v} />
                      <YAxis type="category" dataKey="name" width={110} tick={axisStyle} tickLine={false} />
                      <Tooltip content={<ChartTip fmt={fmt} />} />
                      <Bar dataKey="revenue" name="revenue" fill={BLUE} radius={[0,4,4,0]} isAnimationActive animationDuration={1100} animationEasing="ease-out" />
                      <Bar dataKey="costs" name="costs" fill={RED} radius={[0,4,4,0]} isAnimationActive animationDuration={1100} animationEasing="ease-out" animationBegin={120} />
                    </BarChart>
                  </ResponsiveContainer>
                  <Insight text={t('analytics.insight_event_tip')} />
                </Section>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                  {eventAnalytics.map((ev, i) => (
                    <div key={i} className="an-fade-in" style={{ background:WHITE, borderRadius:'var(--radius-lg)', border:'1px solid '+BORDER, padding:'14px', animationDelay:(i*60)+'ms' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexWrap:'wrap', gap:'6px' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, fontFamily:FONT_UI }}>{ev.name}</div>
                          <div style={{ fontSize: '11.5px', color: MUTED, fontFamily:FONT_BODY }}>{ev.type} · {ev.date ? new Date(ev.date).toLocaleDateString('en-BD', { day:'numeric', month:'short', year:'numeric' }) : ''}</div>
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: 'var(--radius-full)', background: ev.roi > 100 ? getTint(GREEN).bg : ev.roi > 0 ? getTint(AMBER).bg : getTint(RED).bg, color: ev.roi > 100 ? GREEN : ev.roi > 0 ? AMBER : RED, fontFamily:FONT_UI }}>
                          {ev.roi > 0 ? '+' : ''}{ev.roi}% {t('analytics.ev_roi')}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }} className="an-event-stats">
                        {[{ l: t('analytics.ev_revenue'), v: fmt(ev.revenue), c: BLUE }, { l: t('analytics.ev_costs'), v: fmt(ev.costs), c: RED }, { l: t('analytics.ev_profit'), v: fmt(ev.profit), c: ev.profit >= 0 ? GREEN : RED }, { l: t('analytics.ev_orders'), v: ev.orders, c: TEXT }].map(s => (
                          <div key={s.l} style={{ textAlign: 'center', padding: '8px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-muted)' }}>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: s.c, fontFamily:FONT_UI }}>{s.v}</div>
                            <div style={{ fontSize: '10.5px', color: MUTED, marginTop: '2px', fontFamily:FONT_BODY }}>{s.l}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {bestEvent && (
                  <Insight text={
                    t('analytics.insight_best_event') + ' "' + bestEvent.name + '" — ' + fmt(bestEvent.profit) + ' ' + t('analytics.insight_best_event2') + ' ' + bestEvent.roi + '% ' + t('analytics.insight_best_event3') + ' ' +
                    (eventAnalytics.filter(e => e.roi < 0).length > 0
                      ? eventAnalytics.filter(e => e.roi < 0).length + ' ' + t('analytics.insight_event_loss')
                      : t('analytics.insight_event_all_good'))
                  } color={GREEN} />
                )}
              </div>
            )}

            {/* Disclaimer */}
            <div style={{ padding: '14px 16px', borderRadius: 'var(--radius-lg)', background: WHITE, border: '1px solid '+BORDER, fontSize: '11.5px', color: MUTED, lineHeight: 1.7, marginTop: '14px', fontFamily:FONT_BODY }}>
              {t('analytics.disclaimer')}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes an-spin { to { transform: rotate(360deg) } }
        @keyframes an-fade-up { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
        .an-fade-in { animation: an-fade-up 0.5s ease-out both; }
        .an-kpi-card { animation: an-fade-up 0.5s ease-out both; }

        .an-kpi-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
          margin-bottom: 14px;
        }
        @media(max-width:900px) {
          .an-half-grid { grid-template-columns: 1fr !important; }
          .an-kpi-grid {
            display: flex;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            gap: 10px;
            padding-bottom: 6px;
            margin-bottom: 14px;
          }
          .an-kpi-grid::-webkit-scrollbar { display: none; }
          .an-kpi-card {
            flex: 0 0 152px;
            scroll-snap-align: start;
          }
          .an-tabs {
            position: sticky;
            top: 56px;
            z-index: 30;
            background: var(--bg-card);
            box-shadow: 0 2px 6px rgba(0,0,0,0.06);
          }
        }
        @media(max-width:600px) {
          .an-event-stats { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  )
}

export default Analytics